// Firebase imports
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { profileDocRef, publicProfilesCollection } from '@/lib/public-profile';

import {
  IUserRepository,
  CreateUserData,
  UpdateUserData,
  UserFilter,
  GetUserData,
} from './interfaces';
import { AnalyticsRepository, AccountContext } from './analytics.repository';
import { normalizeRoles } from '@/lib/normalize-user-data';
import { DRXError, toDRXError } from '@/lib/drx-error';
import { assertClientMayWriteCollection } from '@/lib/firestore-client-write-guard';

export interface SendUserResetPasswordData {
  email: string;
}

export interface SendUserVerifyEmailData {
  userId: string;
}

export interface ChangeUserPasswordData {
  userId: string;
  newPassword: string;
}

function normalizeUserRecord(id: string, raw: Record<string, any>): GetUserData {
  const roles = normalizeRoles(raw);

  return {
    id,
    ...raw,
    firstName: raw.firstName ?? raw.firstName ?? null,
    lastName: raw.lastName ?? raw.lastName ?? null,
    roles,
    profileImageUrl:
      raw.profileImageUrl ?? raw.profileImage ?? raw.photoURL ?? null,
    authProvider: raw.authProvider ?? raw.authProvider ?? null,
    tenantId: raw.tenantId ?? raw.tenantId ?? undefined,
    isActive: raw.isActive ?? raw.isActive ?? undefined,
    emailVerified: raw.emailVerified ?? raw.emailVerified ?? false,
    googleId: raw.googleId ?? raw.googleId ?? undefined,
    createdAt: raw.createdAt ?? raw.createdAt ?? undefined,
    updatedAt: raw.updatedAt ?? raw.updatedAt ?? undefined,
    lastLoginAt: raw.lastLoginAt ?? raw.lastLogin ?? undefined,
    lastSignInTime: raw.lastSignInTime ?? raw.lastSignInTime ?? undefined,
  };
}

export class UserRepository implements IUserRepository {
  private collectionName = 'users';

  constructor(private analyticsRepo?: AnalyticsRepository) {
    console.log('[UserRepository] Constructor - Collection:', this.collectionName, 'DB instance:', !!db);
  }

  private toRepositoryError(
    error: unknown,
    code: string,
    devMessage: string,
    context?: Record<string, unknown>,
  ) {
    return toDRXError(error, {
      code,
      devMessage,
      context,
    });
  }

  // Create user
  async createUser(userData: CreateUserData, context?: AccountContext): Promise<GetUserData> {
    console.log('[UserRepository.createUser] START - UserData:', userData.email);
    try {
      console.log('[UserRepository.createUser] Creating document with Firebase client SDK');
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...userData,
        accountId: userData.tenantId || context?.accountId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      console.log('[UserRepository.createUser] Document created with ID:', docRef.id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'USER_CREATE_MISSING_DOCUMENT',
          devMessage: 'User document was missing after create',
          context: {
            email: userData.email,
            username: userData.username,
            tenantId: userData.tenantId,
            accountId: context?.accountId,
          },
        });
      }

      const userDataResult = normalizeUserRecord(docRef.id, docSnap.data() as Record<string, any>);

      // Track analytics event
      if (this.analyticsRepo) {
        await this.analyticsRepo.trackAnalyticsEvent(
          'userRegistered',
          {
            userId: userDataResult.id,
            email: userDataResult.email,
            accountId: userDataResult.tenantId || context?.accountId,
          },
          userDataResult.id,
          context
        );

        await this.analyticsRepo.trackEntityActivity(
          'user',
          userDataResult.id,
          'create',
          userDataResult.id,
          {
            email: userDataResult.email,
            registrationMethod: 'email',
          },
          context
        );
      }

      return userDataResult;
    } catch (error) {
      console.error('Error creating user:', error);
      throw this.toRepositoryError(error, 'USER_CREATE_FAILED', 'Failed to create user', {
        email: userData.email,
        username: userData.username,
        tenantId: userData.tenantId,
        accountId: context?.accountId,
        authProvider: (userData as Record<string, unknown>).authProvider,
      });
    }
  }

  // Check if username is available
  async isUsernameAvailable(username: string): Promise<boolean> {
    console.log('[UserRepository.isUsernameAvailable] START - Username:', username);
    try {
      console.log('[UserRepository.isUsernameAvailable] Querying with Firebase client SDK');
      const q = query(
        collection(db, this.collectionName),
        where('username', '==', username)
      );
      const querySnapshot = await getDocs(q);
      
      console.log('[UserRepository.isUsernameAvailable] Query result - Empty:', querySnapshot.empty);
      return querySnapshot.empty;
    } catch (error) {
      console.error('[UserRepository.isUsernameAvailable] ERROR:', error);
      throw this.toRepositoryError(error, 'USER_USERNAME_AVAILABILITY_FAILED', 'Failed to check username availability', {
        username,
      });
    }
  }

  // Get user by ID
  async getUserById(userId: string): Promise<GetUserData | null> {
    console.log('[UserRepository.getUserById] START - UserId:', userId);
    try {
      // Yourself: users/{uid}. Anyone else: their public profile (users/{uid}
      // is owner/admin-only -- it holds email, phone and billing fields).
      const docRef = profileDocRef(userId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        console.log('[UserRepository.getUserById] Document does not exist for userId:', userId);
        return null;
      }

      const userData = normalizeUserRecord(docSnap.id, docSnap.data() as Record<string, any>);
      console.log('[UserRepository.getUserById] SUCCESS - Found user:', userData.id);
      return userData;
    } catch (error) {
      console.error('[UserRepository.getUserById] ERROR:', error);
      throw this.toRepositoryError(error, 'USER_GET_FAILED', 'Failed to get user by ID', {
        userId,
      });
    }
  }

  // Find user by ID using client-side Firebase (for course lookups)
  async findById(userId: string): Promise<GetUserData | null> {
    console.log('[UserRepository.findById] START - UserId:', userId);
    try {
      const docRef = profileDocRef(userId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        console.log('[UserRepository.findById] Document does not exist for userId:', userId);
        return null;
      }

      const userData = normalizeUserRecord(docSnap.id, docSnap.data() as Record<string, any>);
      console.log('[UserRepository.findById] SUCCESS - Found user:', userData.email || userData.username || 'Unknown');
      return userData;
    } catch (error) {
      console.error('[UserRepository.findById] ERROR:', error);
      console.log('[UserRepository.findById] Returning null due to error instead of throwing');
      return null; // Return null instead of throwing error for lookups
    }
  }

  // Get users with filters
  async getUsers(filter: UserFilter = {}, limitCount?: number): Promise<GetUserData[]> {
    console.log('[UserRepository.getUsers] START - Filter:', filter, 'Limit:', limitCount);
    try {
      console.log('[UserRepository.getUsers] Using client-side Firebase to query users collection:', this.collectionName);
      
      // Build query with filters using client-side Firebase syntax
      const constraints = [];
      
      if (filter.email) {
        console.log('[UserRepository.getUsers] Adding email filter:', filter.email);
        constraints.push(where('email', '==', filter.email));
      }
      if (filter.username) {
        console.log('[UserRepository.getUsers] Adding username filter:', filter.username);
        constraints.push(where('username', '==', filter.username));
      }
      if (filter.role) {
        console.log('[UserRepository.getUsers] Adding role filter:', filter.role);
        constraints.push(where('roles', 'array-contains', filter.role));
      }
      if (filter.isActive !== undefined) {
        console.log('[UserRepository.getUsers] Adding isActive filter:', filter.isActive);
        constraints.push(where('isActive', '==', filter.isActive));
      }
      if (filter.emailVerified !== undefined) {
        console.log('[UserRepository.getUsers] Adding emailVerified filter:', filter.emailVerified);
        constraints.push(where('emailVerified', '==', filter.emailVerified));
      }
      if (filter.tenantId) {
        console.log('[UserRepository.getUsers] Adding tenantId filter:', filter.tenantId);
        constraints.push(where('tenantId', '==', filter.tenantId));
      }

      // Filters on public fields only (role, username, active) read the public
      // profiles anyone signed in may list; private ones (email, verification,
      // tenant) need users/, which only admins can query.
      const usesPrivateField = !!filter.email || filter.emailVerified !== undefined || !!filter.tenantId;
      const source = usesPrivateField ? collection(db, this.collectionName) : publicProfilesCollection();

      // Add ordering and limit using client-side Firebase syntax
      console.log('[UserRepository.getUsers] Adding orderBy createdAt desc');
      constraints.push(orderBy('createdAt', 'desc'));
      if (limitCount) {
        console.log('[UserRepository.getUsers] Adding limit:', limitCount);
        constraints.push(limit(limitCount));
      }

      // Build the final query
      const q = query(source, ...constraints);

      console.log('[UserRepository.getUsers] Executing client-side Firebase query');
      const querySnapshot = await getDocs(q);
      console.log('[UserRepository.getUsers] Query executed successfully, docs count:', querySnapshot.size);
      
      const users: GetUserData[] = [];

      querySnapshot.forEach((doc) => {
        const userData = normalizeUserRecord(doc.id, doc.data() as Record<string, any>);
        console.log('[UserRepository.getUsers] Processing user doc:', doc.id, 'email:', userData.email);
        users.push(userData);
      });

      console.log('[UserRepository.getUsers] SUCCESS - Retrieved', users.length, 'users');
      return users;
    } catch (error) {
      console.error('[UserRepository.getUsers] ERROR:', error);
      throw this.toRepositoryError(error, 'USER_LIST_FAILED', 'Failed to get users', {
        filter,
        limitCount,
      });
    }
  }

  // Update user
  async updateUser(userId: string, updateData: UpdateUserData): Promise<GetUserData> {
    console.log('[UserRepository.updateUser] START - UserId:', userId);
    try {
      console.log('[UserRepository.updateUser] Updating document with Firebase client SDK');
      const docRef = doc(db, this.collectionName, userId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: updateData.updatedAt ?? Timestamp.now(),
      });

      console.log('[UserRepository.updateUser] Getting updated document');
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'USER_UPDATE_MISSING_DOCUMENT',
          devMessage: 'User was missing after update',
          context: {
            userId,
            updateData,
          },
        });
      }

      const result = normalizeUserRecord(docSnap.id, docSnap.data() as Record<string, any>);
      console.log('[UserRepository.updateUser] SUCCESS - Updated user:', result.email);
      return result;
    } catch (error) {
      console.error('[UserRepository.updateUser] ERROR:', error);
      throw this.toRepositoryError(error, 'USER_UPDATE_FAILED', 'Failed to update user', {
        userId,
        updateData,
      });
    }
  }

  // Delete user
  async deleteUser(userId: string): Promise<void> {
    console.log('[UserRepository.deleteUser] START - UserId:', userId);
    try {
      console.log('[UserRepository.deleteUser] Deleting document with Firebase client SDK');
      const docRef = doc(db, this.collectionName, userId);
      await deleteDoc(docRef);
      console.log('[UserRepository.deleteUser] SUCCESS - User deleted');
    } catch (error) {
      console.error('[UserRepository.deleteUser] ERROR:', error);
      throw this.toRepositoryError(error, 'USER_DELETE_FAILED', 'Failed to delete user', {
        userId,
      });
    }
  }

  // Send password reset email
  async sendPasswordReset(data: SendUserResetPasswordData): Promise<void> {
    try {
      // Implementation would depend on your auth setup.
      // Do NOT log data.email — leaks PII to browser console.
      void data;
    } catch (error) {
      console.error('Error sending password reset:', error);
      throw this.toRepositoryError(error, 'USER_PASSWORD_RESET_SEND_FAILED', 'Failed to send password reset', {
        email: data.email,
      });
    }
  }

  // Send email verification
  async sendEmailVerification(data: SendUserVerifyEmailData): Promise<void> {
    try {
      // Do NOT log data.userId — leaks PII to browser console.
      void data;
    } catch (error) {
      console.error('Error sending email verification:', error);
      throw this.toRepositoryError(error, 'USER_EMAIL_VERIFICATION_SEND_FAILED', 'Failed to send email verification', {
        userId: data.userId,
      });
    }
  }

  // Change user password
  async changePassword(data: ChangeUserPasswordData): Promise<void> {
    try {
      // Do NOT log data.userId — leaks PII to browser console.
      void data;
    } catch (error) {
      console.error('Error changing password:', error);
      throw this.toRepositoryError(error, 'USER_PASSWORD_CHANGE_FAILED', 'Failed to change password', {
        userId: data.userId,
      });
    }
  }

  // Get user by email
  async getUserByEmail(email: string): Promise<GetUserData | null> {
    console.log('[UserRepository.getUserByEmail] START - Email:', email);
    try {
      console.log('[UserRepository.getUserByEmail] Querying with Firebase client SDK');
      const q = query(
        collection(db, this.collectionName),
        where('email', '==', email)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        console.log('[UserRepository.getUserByEmail] No user found for email:', email);
        return null;
      }

      const doc = querySnapshot.docs[0];
      const result = normalizeUserRecord(doc.id, doc.data() as Record<string, any>);
      console.log('[UserRepository.getUserByEmail] SUCCESS - Found user:', result.email);
      return result;
    } catch (error) {
      console.error('[UserRepository.getUserByEmail] ERROR:', error);
      throw this.toRepositoryError(error, 'USER_GET_BY_EMAIL_FAILED', 'Failed to get user by email', {
        email,
      });
    }
  }

  // Get user by username
  async getUserByUsername(username: string): Promise<GetUserData | null> {
    console.log('[UserRepository.getUserByUsername] START - Username:', username);
    try {
      // Usernames are public: look them up in the public profiles.
      const q = query(
        publicProfilesCollection(),
        where('username', '==', username)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        console.log('[UserRepository.getUserByUsername] No user found for username:', username);
        return null;
      }

      const doc = querySnapshot.docs[0];
      const result = normalizeUserRecord(doc.id, doc.data() as Record<string, any>);
      console.log('[UserRepository.getUserByUsername] SUCCESS - Found user:', result.email);
      return result;
    } catch (error) {
      console.error('[UserRepository.getUserByUsername] ERROR:', error);
      throw this.toRepositoryError(error, 'USER_GET_BY_USERNAME_FAILED', 'Failed to get user by username', {
        username,
      });
    }
  }

  // Get user by google ID
  async getUserByGoogleId(googleId: string): Promise<GetUserData | null> {
    console.log('[UserRepository.getUserByGoogleId] START - GoogleId:', googleId);
    try {
      console.log('[UserRepository.getUserByGoogleId] Querying with Firebase client SDK');
      const q = query(
        collection(db, this.collectionName),
        where('googleId', '==', googleId)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        console.log('[UserRepository.getUserByGoogleId] No user found for GoogleId:', googleId);
        return null;
      }

      const doc = querySnapshot.docs[0];
      const result = normalizeUserRecord(doc.id, doc.data() as Record<string, any>);
      console.log('[UserRepository.getUserByGoogleId] SUCCESS - Found user:', result.email);
      return result;
    } catch (error) {
      console.error('[UserRepository.getUserByGoogleId] ERROR:', error);
      throw this.toRepositoryError(error, 'USER_GET_BY_GOOGLE_ID_FAILED', 'Failed to get user by Google ID', {
        googleId,
      });
    }
  }

  // Get user by auth provider
  async getUsersByAuthProvider(authProvider: string, limitCount?: number): Promise<GetUserData[]> {
    console.log('[UserRepository.getUsersByAuthProvider] START - AuthProvider:', authProvider, 'Limit:', limitCount);
    try {
      console.log('[UserRepository.getUsersByAuthProvider] Querying with Firebase client SDK');
      let q = query(
        collection(db, this.collectionName),
        where('authProvider', '==', authProvider)
      );

      if (limitCount) {
        console.log('[UserRepository.getUsersByAuthProvider] Adding limit:', limitCount);
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      console.log('[UserRepository.getUsersByAuthProvider] Query executed, docs count:', querySnapshot.size);
      const users: GetUserData[] = [];

      querySnapshot.forEach((doc) => {
        const userData = normalizeUserRecord(doc.id, doc.data() as Record<string, any>);
        console.log('[UserRepository.getUsersByAuthProvider] Processing user:', doc.id, userData.email);
        users.push(userData);
      });

      console.log('[UserRepository.getUsersByAuthProvider] SUCCESS - Retrieved', users.length, 'users');
      return users;
    } catch (error) {
      console.error('[UserRepository.getUsersByAuthProvider] ERROR:', error);
      throw this.toRepositoryError(error, 'USER_LIST_BY_AUTH_PROVIDER_FAILED', 'Failed to get users by auth provider', {
        authProvider,
        limitCount,
      });
    }
  }

  // Check if user is admin
  async isUserAdmin(userId: string): Promise<boolean> {
    console.log('[UserRepository.isUserAdmin] START - UserId:', userId);
    try {
      console.log('[UserRepository.isUserAdmin] Calling getUserById');
      const user = await this.getUserById(userId);
      if (!user) {
        console.log('[UserRepository.isUserAdmin] User not found, returning false');
        return false;
      }

      const isAdmin = user.roles.includes('ADMIN');
      console.log('[UserRepository.isUserAdmin] User roles:', user.roles, 'IsAdmin:', isAdmin);
      return isAdmin;
    } catch (error) {
      console.error('[UserRepository.isUserAdmin] ERROR:', error);
      return false;
    }
  }

  // Get all users (admin only)
  async getAllUsers(limitCount?: number): Promise<GetUserData[]> {
    console.log('[UserRepository.getAllUsers] START - Limit:', limitCount);
    try {
      console.log('[UserRepository.getAllUsers] Creating empty filter for all users');
      const filter: UserFilter = {};
      console.log('[UserRepository.getAllUsers] Calling getUsers with empty filter');
      const result = await this.getUsers(filter, limitCount);
      console.log('[UserRepository.getAllUsers] SUCCESS - Retrieved', result.length, 'users');
      return result;
    } catch (error) {
      console.error('[UserRepository.getAllUsers] ERROR:', error);
      throw this.toRepositoryError(error, 'USER_LIST_ALL_FAILED', 'Failed to get all users', {
        limitCount,
      });
    }
  }

  // Upload profile image
  async uploadProfileImage(userId: string, file: File): Promise<string> {
    try {
      // This would typically upload to cloud storage (Firebase Storage, AWS S3, etc.)
      // For now, we'll simulate the upload and return a URL
      console.log('Uploading profile image for user:', userId, 'File:', file.name);

      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Generate a mock URL (in real implementation, this would be from cloud storage)
      const imageUrl = `https://storage.example.com/profiles/${userId}/${Date.now()}_${file.name}`;

      // Update user with new image URL
      await this.updateUser(userId, { profileImageUrl: imageUrl });

      return imageUrl;
    } catch (error) {
      console.error('Error uploading profile image:', error);
      throw this.toRepositoryError(error, 'USER_PROFILE_IMAGE_UPLOAD_FAILED', 'Failed to upload profile image', {
        userId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });
    }
  }

  // Delete profile image
  async deleteProfileImage(userId: string): Promise<void> {
    try {
      const user = await this.getUserById(userId);
      if (!user?.profileImageUrl) {
        return; // No image to delete
      }

      // In real implementation, delete from cloud storage
      console.log('Deleting profile image:', user.profileImageUrl);

      // Update user to remove image URL
      await this.updateUser(userId, { profileImageUrl: null });
    } catch (error) {
      console.error('Error deleting profile image:', error);
      throw this.toRepositoryError(error, 'USER_PROFILE_IMAGE_DELETE_FAILED', 'Failed to delete profile image', {
        userId,
      });
    }
  }

  // Get users by email (for admin operations)
  async getUsersByEmail(email: string): Promise<GetUserData[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('email', '==', email)
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) =>
        normalizeUserRecord(doc.id, doc.data() as Record<string, any>)
      );
    } catch (error) {
      throw new DRXError({
        code: 'USER/GET_BY_EMAIL_FAILED',
        devMessage: `Failed to get users by email: ${error}`,
        userMessage: 'Failed to look up users by email.',
      });
    }
  }

  // Update canonical subscription doc tier
  async updateUserSubscriptionTier(userId: string, tier: string): Promise<void> {
    void userId;
    void tier;
    assertClientMayWriteCollection('subscriptions', 'updateUserSubscriptionTier');
  }

  // Search users by email or username (exact match)
  async searchUsersByEmailOrUsername(identifier: string): Promise<GetUserData | null> {
    try {
      // Try email first
      let usersSnapshot = await getDocs(
        query(collection(db, this.collectionName), where('email', '==', identifier), limit(1))
      );

      // If not found by email, try username
      if (usersSnapshot.empty) {
        usersSnapshot = await getDocs(
          query(collection(db, this.collectionName), where('username', '==', identifier), limit(1))
        );
      }

      if (usersSnapshot.empty) {
        return null;
      }

      const userDoc = usersSnapshot.docs[0];
      return normalizeUserRecord(userDoc.id, userDoc.data() as Record<string, any>);
    } catch (error) {
      throw new DRXError({
        code: 'USER/SEARCH_FAILED',
        devMessage: `Failed to search users by email or username: ${error}`,
        userMessage: 'Failed to search for user.',
      });
    }
  }

  // Search users by display name prefix with optional role filter
  async searchUsers(searchQuery: string, role?: string): Promise<GetUserData[]> {
    try {
      if (!searchQuery.trim()) return [];

      // Every word must start one of the person's names or their username,
      // whatever the capitals: "Frankie Lowe", "fran", "lowe". Firestore
      // prefix queries are case-sensitive and match one field, so a full name
      // found nobody and lower case only worked through a 200-profile sample.
      const words = searchQuery.trim().split(/\s+/).filter(Boolean).map((w) => w.toLowerCase());
      const fieldsOf = (u: GetUserData) => {
        const r = u as unknown as Record<string, unknown>;
        return [u.firstName, u.lastName, u.username, r.name]
          .flatMap((v) => String(v || '').toLowerCase().split(/\s+/))
          .filter(Boolean);
      };
      const matches = (u: GetUserData) => words.every((w) => fieldsOf(u).some((f) => f.startsWith(w)));

      // Candidates: names/usernames starting with the longest word, in the
      // capitalisations people use (fran, Fran, FRAN).
      const key = [...words].sort((a, b) => b.length - a.length)[0];
      const variants = Array.from(new Set([key, key[0].toUpperCase() + key.slice(1), key.toUpperCase()]));
      const resultById = new Map<string, GetUserData>();
      await Promise.all(
        (['username', 'firstName', 'lastName'] as const).flatMap((field) =>
          variants.map(async (v) => {
            const snapshot = await getDocs(query(
              publicProfilesCollection(),
              where(field, '>=', v),
              where(field, '<=', v + '\uf8ff'),
              limit(25),
            ));
            snapshot.docs.forEach((docSnap) => {
              resultById.set(docSnap.id, normalizeUserRecord(docSnap.id, docSnap.data() as Record<string, any>));
            });
          })
        )
      );
      let users = Array.from(resultById.values()).filter(matches);

      // Odd capitals ("mcDonald") miss the prefix queries: look through a
      // sample of public profiles before saying nobody matched.
      if (users.length === 0) {
        const sample = await getDocs(query(publicProfilesCollection(), limit(500)));
        users = sample.docs
          .map((d) => normalizeUserRecord(d.id, d.data() as Record<string, any>))
          .filter(matches)
          .slice(0, 20);
      }

      if (role) {
        users = users.filter((user) => user.roles.includes(role));
      }

      return users;
    } catch (error) {
      throw new DRXError({
        code: 'USER/SEARCH_USERS_FAILED',
        devMessage: `Failed to search users: ${error}`,
        userMessage: 'Failed to search users.',
      });
    }
  }

  // Update user roles
  async updateUserRole(userId: string, roles: string[]): Promise<{ id: string; roles: string[] }> {
    try {
      const docRef = doc(db, this.collectionName, userId);
      await updateDoc(docRef, {
        roles,
        updatedAt: serverTimestamp(),
      });
      return { id: userId, roles };
    } catch (error) {
      throw new DRXError({
        code: 'USER/UPDATE_ROLE_FAILED',
        devMessage: `Failed to update user role for ${userId}: ${error}`,
        userMessage: 'Failed to update user role.',
      });
    }
  }

  async getUserFeatures(userId: string): Promise<string[]> {
    try {
      if (!userId) return [];
      const userDoc = await getDoc(doc(db, this.collectionName, userId));
      if (!userDoc.exists()) return [];
      return userDoc.data().features || [];
    } catch (error) {
      throw new DRXError({
        code: 'USER/FEATURES_FETCH_FAILED',
        devMessage: `Failed to fetch features for user ${userId}`,
        cause: error,
      });
    }
  }

  async getTopInstructors(limitCount: number = 4): Promise<any[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('role', 'in', ['instructor', 'admin']),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      throw new DRXError({
        code: 'USER/TOP_INSTRUCTORS_FAILED',
        devMessage: 'Failed to fetch top instructors',
        cause: error,
      });
    }
  }

  async getActiveConnections(
    limitCount: number = 500,
  ): Promise<Array<{ id: string } & Record<string, unknown>>> {
    try {
      const q = query(
        collection(db, 'connections'),
        where('isActive', '==', true),
        limit(limitCount),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      throw this.toRepositoryError(error, 'USER/CONNECTIONS_FETCH_FAILED', 'Failed to fetch active connections', {
        limitCount,
      });
    }
  }
}
